import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, CheckCircle, XCircle } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const EmailTester = ({ user }) => {
  const [recipient, setRecipient] = useState(user?.email || 'jake.myers@vpgcentral.com');
  const [eventType, setEventType] = useState('password_reset');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const emailTemplates = [
    { value: 'password_reset', label: 'Password Reset' },
    { value: 'welcome_email', label: 'Welcome Email' },
    { value: 'temporary_password_email', label: 'Temporary Password' },
    { value: 'password_expiry_warning_30_days', label: 'Password Expiry - 30 Days' },
    { value: 'password_expiry_warning_14_days', label: 'Password Expiry - 14 Days' },
    { value: 'password_expiry_warning_7_days', label: 'Password Expiry - 7 Days' },
    { value: 'password_expiry_warning_3_days', label: 'Password Expiry - 3 Days' },
    { value: 'password_expiry_warning_1_days', label: 'Password Expiry - 1 Day (URGENT)' }
  ];

  // Update recipient when user context loads
  useEffect(() => {
    if (user?.email) {
      setRecipient(user.email);
    }
  }, [user]);

  const handleSendEmail = async () => {
    try {
      setLoading(true);
      setResult(null);
      
      const payload = {
        user: {
          id: user?.id || 1,
          email: recipient,
          full_name: user?.full_name || user?.first_name || 'Test User'
        },
        reset_url: 'http://localhost:5173/reset-password?token=sample-token',
        valid_hours: 1
      };

      const response = await api.post('/api/dev/send-test-email/', {
        recipient,
        event_type: eventType,
        payload
      });

      setResult({
        success: true,
        message: response.data.message || 'Email sent successfully!'
      });

      toast.success('Test email sent successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send email';
      setResult({
        success: false,
        message: errorMessage
      });
      toast.error(`Failed to send email: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email Configuration */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Email Configuration</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Email
              </label>
              <input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter recipient email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Template
              </label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {emailTemplates.map(template => (
                  <option key={template.value} value={template.value}>
                    {template.label}
                  </option>
                ))}
              </select>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSendEmail}
              disabled={loading || !recipient}
              className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center space-x-2 ${
                loading || !recipient
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white'
              } transition-all duration-200`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Send size={18} />
                  <span>Send Test Email</span>
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Template Preview */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Template Info</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Mail size={16} className="text-gray-500 mr-2" />
              <span className="font-medium text-gray-700">
                {emailTemplates.find(t => t.value === eventType)?.label || eventType}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              This will send a test {eventType.replace(/_/g, ' ')} email to {recipient} 
              with sample data for testing purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Result Display */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border-l-4 ${
            result.success 
              ? 'bg-green-50 border-green-500' 
              : 'bg-red-50 border-red-500'
          }`}
        >
          <div className="flex items-center">
            {result.success ? (
              <CheckCircle className="text-green-500 mr-2" size={20} />
            ) : (
              <XCircle className="text-red-500 mr-2" size={20} />
            )}
            <span className={`font-medium ${
              result.success ? 'text-green-800' : 'text-red-800'
            }`}>
              {result.success ? 'Success' : 'Error'}
            </span>
          </div>
          <p className={`mt-1 text-sm ${
            result.success ? 'text-green-700' : 'text-red-700'
          }`}>
            {result.message}
          </p>
        </motion.div>
      )}

      {/* Guidelines */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-semibold text-blue-800 mb-2">Email Testing Guidelines:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Check that emails are delivered to the correct recipient</li>
          <li>• Verify email content matches the template</li>
          <li>• Test with different email addresses and user types</li>
          <li>• Ensure links in emails are functional and secure</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailTester;
